import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Activity, Shield } from "lucide-react";
import { VersionBadge } from "@/components/VersionBadge";

const LoginPage = () => {
  const navigate = useNavigate();
  const { loginWithSSO, user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);

  // If the user was bounced here by the 401 interceptor, tell them why.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("reason") === "expired") {
      toast.info("Your session expired. Please sign in again.");
    }
  }, []);

  // Redirect if already logged in - therapist takes priority
  useEffect(() => {
    if (user && !authLoading) {
      if (user.roles?.includes("therapist")) {
        navigate("/therapist", { replace: true });
      } else if (user.roles?.includes("facilitator")) {
        navigate("/facilitator", { replace: true });
      } else if (user.roles?.includes("admin")) {
        navigate("/admin", { replace: true });
      }
    }
  }, [user, authLoading, navigate]);

  const handleSSOLogin = () => {
    setLoading(true);
    loginWithSSO();
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex bg-cover bg-center relative"
        style={{ 
          backgroundImage: "url('https://images.pexels.com/photos/4269491/pexels-photo-4269491.jpeg')" 
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-primary/90 to-primary/70" />
        <div className="relative z-10 flex flex-col justify-between h-full p-12 text-white">
          <div className="flex items-center gap-4">
            <img src="/tmc-logo.png" alt="TMC TeleTech" className="h-32 w-auto" />
          </div>
          
          <div className="max-w-md">
            <h1 className="text-4xl font-bold font-['Manrope'] mb-4">
              National Telehealth Platform
            </h1>
            <p className="text-white/80 text-lg">
              Connecting patients with therapists through our innovative scheduling platform. 
              Experience seamless healthcare delivery with just-in-time matching.
            </p>
          </div>

          {/* Footer spacer (TMC Support Suite branding removed per product request) */}
          <div />
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/tmc-logo.png" alt="TMC TeleTech" className="h-28 w-auto" />
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold font-['Manrope'] tracking-tight">Welcome to TMC TeleTech</h2>
            <p className="text-muted-foreground mt-1">Sign in with your organization account to continue</p>
          </div>

          <Card className="border-0 shadow-none">
            <CardHeader className="px-0 pt-0">
              <CardTitle className="text-lg">Staff Login</CardTitle>
              <CardDescription>
                For therapists, facilitators, and administrators
              </CardDescription>
            </CardHeader>
            <CardContent className="px-0">
              {/* SSO Button - Primary method */}
              <Button
                type="button"
                className="w-full h-14 gap-3 text-base"
                onClick={handleSSOLogin}
                disabled={loading}
                data-testid="sso-login-btn"
              >
                <svg width="24" height="24" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                </svg>
                {loading ? "Redirecting..." : "Sign in with Microsoft"}
              </Button>

              <div className="mt-6 p-4 bg-secondary/50 rounded-lg flex items-start gap-3">
                <Shield className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium">Microsoft Entra ID SSO</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Use your @therapymgmt.com Microsoft account for secure single sign-on access.
                    All staff members must authenticate through the organization&apos;s identity provider.
                  </p>
                </div>
              </div>

              <div className="mt-6 text-center">
                <p className="text-sm text-muted-foreground">
                  Having trouble signing in? Contact your administrator or IT support.
                </p>
                <div className="mt-3">
                  <VersionBadge />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
